import { Check, Search } from '@mui/icons-material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { accents } from 'sutra/tokens/colors'

import { FilterPopoverFreeInputPane } from 'features/charts-v2/components/FilterPopoverFreeInputPane/FilterPopoverFreeInputPane'
import {
  POPOVER_SHADOW,
  useFixedPopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement'
import { deduplicateFilterValues } from 'features/charts-v2/utils/filter-dedup'
import { sortFilterOptionValues } from 'features/charts-v2/utils/sort-filter-option-values'
import {
  FilterOption,
  FilterOptionSelect,
  FilterOptionValue,
  FilterTypes,
} from 'features/filters/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'
import {
  ALIAS_FOR_NULL_VALUES,
  isNullValueAlias,
} from 'src/constants/filter-values'

interface OptionProps {
  checked: boolean
  onClick: () => void
  label: string
  isGroupHeader?: boolean
  isHighlighted?: boolean
}

const Option = React.forwardRef<HTMLDivElement, OptionProps>(
  (
    { checked, onClick, label, isGroupHeader = false, isHighlighted = false },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
      <div
        ref={ref}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 4px',
          cursor: 'pointer',
          backgroundColor: isHighlighted
            ? accents.reflectionBlue[100]
            : isHovered
              ? accents.reflectionBlue[100]
              : 'transparent',
          borderRadius: '4px',
          transition: 'background-color 0.15s ease',
        }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            width: '16px',
            height: '16px',
            border: checked ? 'none' : '1.5px solid #d1d5db',
            borderRadius: '3px',
            marginRight: '8px',
            backgroundColor: checked ? accents.reflectionBlue[500] : 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {checked && <Check sx={{ color: 'white', fontSize: 14 }} />}
        </div>
        <span
          style={{
            fontSize: '13px',
            ...(isGroupHeader && {
              color: accents.reflectionBlue[500],
              fontWeight: '600',
            }),
          }}
        >
          {label}
        </span>
      </div>
    )
  },
)

Option.displayName = 'Option'

interface FilterPopoverProps {
  filter: FilterOption
  isOpen: boolean
  onClose: () => void
  onSelectionChange: (selectedValues: string[]) => void
  currentSelectedValues?: string[]
  cardRef?: React.RefObject<HTMLElement>
  // When true, skip the selected-first bucketing so rows render in their
  // intrinsic order (e.g. numeric rating values 1..5 stay 1..5 regardless
  // of which boxes are checked).
  disableSelectedFirstSort?: boolean
}

// Stable identity prevents `useEffect` re-fire on every parent render
// when callers omit `currentSelectedValues`.
const EMPTY_SELECTED_VALUES: readonly string[] = []

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  filter,
  isOpen,
  onClose,
  onSelectionChange,
  currentSelectedValues = EMPTY_SELECTED_VALUES as string[],
  cardRef,
  disableSelectedFirstSort = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectMyBrands, setSelectMyBrands] = useState(false)
  const [selectCompetitorBrands, setSelectCompetitorBrands] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const popoverRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const { placement, isMeasured, style } = useFixedPopoverPlacement({
    anchorRef: cardRef,
    popoverRef,
    isOpen,
  })

  // Initialize selectedValues with currentSelectedValues when popover opens
  useEffect(() => {
    if (isOpen) {
      setSelectedValues(currentSelectedValues)
      setSearchTerm('')
      // Reset highlight to the search input so a fresh open never inherits
      // a stale row highlight from a prior open (the component doesn't unmount).
      setHighlightedIndex(-1)
      // Update selectAll state based on current selection
      const selectFilter = filter as FilterOptionSelect
      const allValues = selectFilter.values?.map((v) => v.value) || []
      setSelectAll(
        currentSelectedValues.length === allValues.length &&
          allValues.length > 0,
      )
    }
  }, [isOpen, currentSelectedValues, filter])

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        cardRef?.current &&
        !cardRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose, cardRef])

  useEscapeDismiss({ isOpen, onClose })

  const selectFilter = filter as FilterOptionSelect

  // Determine if this is a theme or dataset filter (uses cluster_uuid as identifier)
  const isThemeFilter = filter.field === 'theme' || filter.field === 'proj_uuid'

  // For theme and dataset filters, de-duplicate options by display name first
  // Multiple cluster_uuids with the same display name are merged
  const dedupedValues = useMemo(() => {
    if (isThemeFilter && selectFilter.values) {
      return deduplicateFilterValues(selectFilter.values)
    }
    return selectFilter.values || []
  }, [isThemeFilter, selectFilter.values])

  // Memoize filtered values
  const filteredValues = useMemo(
    () =>
      dedupedValues.filter((value: FilterOptionValue) =>
        value.value.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [dedupedValues, searchTerm],
  )

  // Determine if this is a brand filter and categorize brands
  const isBrandFilter =
    filter.field === 'brand' || filter.field === 'product_brand'
  const customerBrands = useMemo(
    () => selectFilter.customer_brands || [],
    [selectFilter.customer_brands],
  )

  const myBrands = useMemo(
    () =>
      isBrandFilter
        ? filteredValues.filter((v) => customerBrands.includes(v.value))
        : [],
    [isBrandFilter, filteredValues, customerBrands],
  )

  const competitorBrands = useMemo(
    () =>
      isBrandFilter
        ? filteredValues.filter((v) => !customerBrands.includes(v.value))
        : [],
    [isBrandFilter, filteredValues, customerBrands],
  )

  // Helper to get the identifier for a filter value
  // For theme filters, uses cluster_uuid; for others, uses value
  // For de-duped theme rows, returns the first identifier
  const getValueIdentifier = useCallback(
    (value: FilterOptionValue): string => {
      if (isThemeFilter) {
        // For de-duped rows, identifiers[0] is the primary identifier
        return value.identifiers?.[0] ?? value.cluster_uuid ?? value.value
      }
      return value.value
    },
    [isThemeFilter],
  )

  // Check if a de-duped row is selected
  // For themes with multiple identifiers, check if ANY are selected
  const isValueSelected = useCallback(
    (value: FilterOptionValue): boolean => {
      if (isThemeFilter && value.identifiers && value.identifiers.length > 0) {
        return value.identifiers.some((id) => selectedValues.includes(id))
      }
      return selectedValues.includes(getValueIdentifier(value))
    },
    [isThemeFilter, selectedValues, getValueIdentifier],
  )

  // Build navigable items list for keyboard navigation
  type NavigableItem =
    | { type: 'myBrands' }
    | { type: 'competitorBrands' }
    | { type: 'selectAll' }
    | { type: 'value'; identifier: string; value: FilterOptionValue }

  const navigableItems = useMemo<NavigableItem[]>(() => {
    const items: NavigableItem[] = []

    // Add brand grouping options if applicable
    if (isBrandFilter) {
      if (myBrands.length > 0) {
        items.push({ type: 'myBrands' })
      }
      if (competitorBrands.length > 0) {
        items.push({ type: 'competitorBrands' })
      }
    }

    // Add select all option
    items.push({ type: 'selectAll' })

    // Selected first; count-desc within each bucket (alphabetical
    // tie-break / alphabetical when any count is still loading).
    // When `disableSelectedFirstSort` is set, skip the selected-first
    // bucketing so callers like the rating filter can render rows in
    // their intrinsic order regardless of which are checked.
    const sortedValues = sortFilterOptionValues(
      filteredValues,
      disableSelectedFirstSort ? undefined : isValueSelected,
    )

    sortedValues.forEach((value) => {
      items.push({
        type: 'value',
        identifier: getValueIdentifier(value),
        value,
      })
    })

    return items
  }, [
    isBrandFilter,
    myBrands,
    competitorBrands,
    filteredValues,
    getValueIdentifier,
    isValueSelected,
    disableSelectedFirstSort,
  ])

  // Reset highlight to "none" when the list or search term changes so the
  // search input keeps keyboard focus on open. The user must ArrowDown to
  // move highlight into the list (first stop: "Select all in list").
  useEffect(() => {
    setHighlightedIndex(-1)
  }, [navigableItems.length, searchTerm])

  // Scroll highlighted item into view, or refocus the search input when
  // the user arrows back up to position -1 (no row highlighted).
  useEffect(() => {
    if (highlightedIndex === -1) {
      searchInputRef.current?.focus()
    } else if (highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [highlightedIndex])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (navigableItems.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        // Cycle forward; past the last row wraps back to the search input
        // (highlight -1 = input focused, nothing highlighted).
        setHighlightedIndex((prev) =>
          prev < navigableItems.length - 1 ? prev + 1 : -1,
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        // Cycle backward; from the search input (-1) wrap to the last row,
        // and from the first row (0) return to the search input.
        setHighlightedIndex((prev) =>
          prev <= -1 ? navigableItems.length - 1 : prev - 1,
        )
        break
      case ' ':
        // Only treat Space as a toggle once the user has navigated into the
        // list. With nothing highlighted (highlightedIndex === -1) the search
        // input is focused, so let the space type normally.
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          event.preventDefault()
          const item = navigableItems[highlightedIndex]
          if (item) handleNavigableItemToggle(item)
        }
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (item) handleNavigableItemToggle(item)
        }
        handleAdd()
        break
      // Escape is handled by useEscapeDismiss above — it needs to work
      // regardless of whether this popover holds DOM focus, so it isn't
      // duplicated here.
    }
  }

  const handleNavigableItemToggle = (item: NavigableItem): void => {
    switch (item.type) {
      case 'myBrands':
        handleSelectMyBrands()
        break
      case 'competitorBrands':
        handleSelectCompetitorBrands()
        break
      case 'selectAll':
        handleSelectAll()
        break
      case 'value':
        handleValueToggle(item.identifier)
        break
    }
  }

  // Update master checkbox states based on selected values
  useEffect(() => {
    if (isBrandFilter && myBrands.length > 0) {
      const allMyBrandsSelected = myBrands.every((b) =>
        selectedValues.includes(b.value),
      )
      setSelectMyBrands(allMyBrandsSelected)
    }
    if (isBrandFilter && competitorBrands.length > 0) {
      const allCompetitorBrandsSelected = competitorBrands.every((b) =>
        selectedValues.includes(b.value),
      )
      setSelectCompetitorBrands(allCompetitorBrandsSelected)
    }
  }, [selectedValues, isBrandFilter, myBrands, competitorBrands])

  // Only show popover for SELECT type filters
  if (filter.filter_type !== FilterTypes.SELECT) {
    return null
  }

  const handleSelectAll = (): void => {
    if (selectAll) {
      setSelectedValues([])
      setSelectAll(false)
    } else {
      // For theme filters with de-duped rows, collect ALL identifiers
      const allValues: string[] = []
      filteredValues.forEach((v: FilterOptionValue) => {
        if (isThemeFilter && v.identifiers && v.identifiers.length > 0) {
          allValues.push(...v.identifiers)
        } else {
          allValues.push(getValueIdentifier(v))
        }
      })
      setSelectedValues(allValues)
      setSelectAll(true)
    }
  }

  const handleSelectMyBrands = (): void => {
    const myBrandValues = myBrands.map((b) => b.value)
    if (selectMyBrands) {
      // Uncheck all my brands
      setSelectedValues((prev) =>
        prev.filter((v) => !myBrandValues.includes(v)),
      )
    } else {
      // Check all my brands
      setSelectedValues((prev) => [
        ...prev.filter((v) => !myBrandValues.includes(v)),
        ...myBrandValues,
      ])
    }
  }

  const handleSelectCompetitorBrands = (): void => {
    const competitorBrandValues = competitorBrands.map((b) => b.value)
    if (selectCompetitorBrands) {
      // Uncheck all competitor brands
      setSelectedValues((prev) =>
        prev.filter((v) => !competitorBrandValues.includes(v)),
      )
    } else {
      // Check all competitor brands
      setSelectedValues((prev) => [
        ...prev.filter((v) => !competitorBrandValues.includes(v)),
        ...competitorBrandValues,
      ])
    }
  }

  const handleValueToggle = (identifier: string): void => {
    // Find the value object to get all its identifiers (for de-duped themes)
    const valueObj = filteredValues.find(
      (v) => getValueIdentifier(v) === identifier,
    )

    let newSelectedValues: string[]
    if (
      isThemeFilter &&
      valueObj?.identifiers &&
      valueObj.identifiers.length > 0
    ) {
      // Toggle all identifiers for this de-duped row
      const allIds = valueObj.identifiers
      const anySelected = allIds.some((id) => selectedValues.includes(id))

      if (anySelected) {
        // Remove all identifiers
        newSelectedValues = selectedValues.filter((v) => !allIds.includes(v))
      } else {
        // Add all identifiers
        newSelectedValues = [...selectedValues, ...allIds]
      }
    } else {
      // Normal single-value toggle
      newSelectedValues = selectedValues.includes(identifier)
        ? selectedValues.filter((v) => v !== identifier)
        : [...selectedValues, identifier]
    }

    setSelectedValues(newSelectedValues)

    // Update selectAll state
    const allRequiredIds: string[] = []
    filteredValues.forEach((v) => {
      if (isThemeFilter && v.identifiers && v.identifiers.length > 0) {
        allRequiredIds.push(...v.identifiers)
      } else {
        allRequiredIds.push(getValueIdentifier(v))
      }
    })
    setSelectAll(
      allRequiredIds.length > 0 &&
        allRequiredIds.every((id) => newSelectedValues.includes(id)),
    )
  }

  const handleAdd = (): void => {
    onSelectionChange(selectedValues)
    onClose()
  }

  if (!isOpen) {
    return null
  }

  const popoverStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '12px',
    maxHeight: 350,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: POPOVER_SHADOW[placement],
    zIndex: 1001,
    border: '1px solid #d8d8d8',
    visibility: isMeasured ? 'visible' : 'hidden',
    ...style,
  }

  // No values shipped for this select field: the backend withheld them above
  // the filter-options count limit, so there is no list to search or tick.
  // Take typed exact values instead (same pane as FilterSelectionPopover).
  if (!isThemeFilter && dedupedValues.length === 0) {
    return createPortal(
      <div ref={popoverRef} data-yogi-overlay="true" style={popoverStyle}>
        <FilterPopoverFreeInputPane
          committedValues={currentSelectedValues}
          onApply={(values) => {
            onSelectionChange(values)
            onClose()
          }}
          onClear={() => {
            onSelectionChange([])
            onClose()
          }}
        />
      </div>,
      document.body,
    )
  }

  return createPortal(
    <div ref={popoverRef} data-yogi-overlay="true" style={popoverStyle}>
      {/* Search */}
      <div style={{ marginBottom: '8px', position: 'relative' }}>
        <Search
          sx={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af',
            fontSize: '18px',
            pointerEvents: 'none',
          }}
        />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          autoFocus
          style={{
            width: '100%',
            padding: '8px 12px 8px 34px',
            border: isSearchFocused
              ? `2px solid ${accents.reflectionBlue[500]}`
              : '2px solid #d9d9d9',
            borderRadius: '6px',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Options list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          marginBottom: '8px',
        }}
      >
        {navigableItems.map((item, index) => {
          const isHighlighted = index === highlightedIndex

          if (item.type === 'myBrands') {
            return (
              <Option
                key="myBrands"
                ref={isHighlighted ? highlightedItemRef : null}
                checked={selectMyBrands}
                onClick={handleSelectMyBrands}
                label="My Brands"
                isGroupHeader
                isHighlighted={isHighlighted}
              />
            )
          }

          if (item.type === 'competitorBrands') {
            return (
              <React.Fragment key="competitorBrands">
                <Option
                  ref={isHighlighted ? highlightedItemRef : null}
                  checked={selectCompetitorBrands}
                  onClick={handleSelectCompetitorBrands}
                  label="Competitor Brands"
                  isGroupHeader
                  isHighlighted={isHighlighted}
                />
                {/* Divider after competitor brands */}
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#e5e7eb',
                    margin: '8px 4px',
                  }}
                />
              </React.Fragment>
            )
          }

          if (item.type === 'selectAll') {
            return (
              <Option
                key="selectAll"
                ref={isHighlighted ? highlightedItemRef : null}
                checked={selectAll}
                onClick={handleSelectAll}
                label={`Select all in list (${filteredValues.length})`}
                isHighlighted={isHighlighted}
              />
            )
          }

          if (item.type === 'value') {
            const isSelected = isValueSelected(item.value)
            const displayValue = item.value.value

            return (
              <Option
                key={item.identifier}
                ref={isHighlighted ? highlightedItemRef : null}
                checked={isSelected}
                onClick={() => handleValueToggle(item.identifier)}
                label={
                  displayValue === null || isNullValueAlias(displayValue)
                    ? ALIAS_FOR_NULL_VALUES
                    : displayValue
                }
                isHighlighted={isHighlighted}
              />
            )
          }

          return null
        })}
      </div>

      {/* Add button */}
      <button
        onClick={handleAdd}
        style={{
          backgroundColor: accents.reflectionBlue[500],
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        Apply
      </button>
    </div>,
    document.body,
  )
}
